import React, { useMemo } from 'react';

import { AnalyticQuery } from '@candu-ai/analytics/types';
import { createArrayOfDate, fillEmptyRecords } from '@candu-ai/analytics/utils';

import { TimeChart, TimeChartProps } from './TimeChart';

interface TimeChartFromRawDataProps<T = { date: string; [e: string]: unknown }>
  extends Omit<TimeChartProps, 'chartData' | 'xAxisDataKey'> {
  rawData: AnalyticQuery<T>[];
}

export const TimeChartFromRawData = ({
  rawData,
  entries,
  ...otherProps
}: TimeChartFromRawDataProps) => {
  const [xAxisId, yAxisId] = rawData[0]?.fields || [];

  const chartData = useMemo(() => {
    if (!rawData[0]?.metadata) {
      return [];
    }

    // create a date xAxis from time of the first entry
    const xLine = createArrayOfDate(rawData[0]?.metadata.time);

    // append 0 entry for non-existing dates
    const transformedRows = rawData.map((raw) => fillEmptyRecords(raw));

    return xLine?.map((xEntry, xIdx) => ({
      [xAxisId.name]: xEntry,
      ...Object.fromEntries(
        rawData.map((res, idx) => [
          entries[idx].value ?? '',
          transformedRows[idx].rows[xIdx]?.[yAxisId.name] ?? 0,
        ]),
      ),
      ...Object.fromEntries(
        rawData.map(() => [
          'data',
          [transformedRows[0]?.rows[xIdx], transformedRows[1]?.rows[xIdx]],
        ]),
      ),
    }));
  }, [rawData, entries, xAxisId, yAxisId]);

  return (
    <TimeChart
      executedAt={rawData[0]?.executedAt}
      chartData={chartData}
      entries={entries}
      xAxisDataKey={xAxisId?.name}
      {...otherProps}
    />
  );
};
